import { useState } from "react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { Trash2, AlertTriangle, AlertCircle, CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import { showNotification } from "../../../store/Central/selectors";
import { Button } from "../../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { isDeveloper } from "@/lib/utils/is-app-user";

const SUPABASE_URL: string = `https://${import.meta.env.VITE_PROD_SUPABASE_PROJECT_URL}.supabase.co`;
const SUPABASE_SERVICE_ROLE_KEY: string = import.meta.env.VITE_PROD_SUPABASE_SERVICE_ROLE_KEY;

interface BucketPaths {
	bucket_name: string;
	paths: string[];
}

interface DeleteResult {
	bucket: string;
	path: string;
	success: boolean;
	error?: string;
}

const EXAMPLE_JSON = `[
  {
    "bucket_name": "v1",
    "paths": [
      "uuid/filename1",
      "uuid/filename2"
    ]
  }
]`;

export const StorageCleanupPage = () => {
	const navigate = useNavigate();
	const [jsonInput, setJsonInput] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);
	const [results, setResults] = useState<DeleteResult[]>([]);
	const [parseError, setParseError] = useState<string | null>(null);

	if (!SUPABASE_SERVICE_ROLE_KEY) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Support", link: "/support" }, { name: "Zugriff verweigert" }]}
						icon={<AlertCircle width="16px" height="16px" color="rgb(220, 38, 38)" />}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">
					<AlertCircle className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Zugriff verweigert
					</h2>
					<p className="text-gray-600">
						Der Service Role Key ist nicht definiert.
					</p>
				</div>
			</div>
		);
	}

	if (!isDeveloper(import.meta.env)) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Support", link: "/support" }, { name: "Zugriff verweigert" }]}
						icon={<AlertCircle width="16px" height="16px" color="rgb(220, 38, 38)" />}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">
					<AlertCircle className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Nur auf Localhost verfügbar
					</h2>
					<p className="text-gray-600 mb-6">
						Diese Seite ist nur auf localhost zugänglich.
					</p>
					<Button
						onClick={() => navigate("/support")}
						variant="outline"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Zurück zu Support
					</Button>
				</div>
			</div>
		);
	}

	const supabase = createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY);

	const handleJsonChange = (value: string) => {
		setJsonInput(value);
		setParseError(null);
		setResults([]);
	};

	const validateJson = (): BucketPaths[] | null => {
		if (!jsonInput.trim()) {
			setParseError("JSON-Eingabe ist leer");
			return null;
		}

		try {
			const parsed = JSON.parse(jsonInput);

			if (!Array.isArray(parsed)) {
				setParseError("JSON muss ein Array sein");
				return null;
			}

			for (const item of parsed) {
				if (!item.bucket_name || typeof item.bucket_name !== "string") {
					setParseError("Jedes Element muss 'bucket_name' als String haben");
					return null;
				}
				if (!Array.isArray(item.paths)) {
					setParseError("Jedes Element muss 'paths' als Array haben");
					return null;
				}
				for (const path of item.paths) {
					if (typeof path !== "string") {
						setParseError("Alle Pfade müssen Strings sein");
						return null;
					}
				}
			}

			return parsed;
		} catch {
			setParseError("Ungültiges JSON-Format");
			return null;
		}
	};

	const getTotalPaths = (): number => {
		try {
			const parsed = JSON.parse(jsonInput) as BucketPaths[];
			return parsed.reduce((acc, item) => acc + item.paths.length, 0);
		} catch {
			return 0;
		}
	};

	const handleDelete = async () => {
		const bucketPaths = validateJson();
		if (!bucketPaths) return;

		setIsDeleting(true);
		setResults([]);

		const allResults: DeleteResult[] = [];

		for (const bucket of bucketPaths) {
			const { data, error } = await supabase.storage
				.from(bucket.bucket_name)
				.remove(bucket.paths);

			if (error) {
				for (const path of bucket.paths) {
					allResults.push({
						bucket: bucket.bucket_name,
						path,
						success: false,
						error: error.message,
					});
				}
			} else {
				const deletedPaths = new Set(data?.map((f) => f.name) ?? []);
				for (const path of bucket.paths) {
					allResults.push({
						bucket: bucket.bucket_name,
						path,
						success: deletedPaths.has(path) || data?.length === bucket.paths.length,
					});
				}
			}
		}

		setResults(allResults);
		setIsDeleting(false);

		const successCount = allResults.filter((r) => r.success).length;
		const failCount = allResults.length - successCount;

		if (failCount === 0) {
			showNotification({
				message: `${successCount} Dateien erfolgreich gelöscht`,
				type: "success",
			});
		} else {
			showNotification({
				message: `${successCount} gelöscht, ${failCount} fehlgeschlagen`,
				type: failCount === allResults.length ? "error" : "warning",
			});
		}
	};

	const successCount = results.filter((r) => r.success).length;
	const failCount = results.filter((r) => !r.success).length;

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Support", link: "/support" }, { name: "Storage Cleanup" }]}
					icon={<Trash2 width="16px" height="16px" color="rgb(55, 65, 81)" />}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-bold text-gray-900">Storage Cleanup</h1>
					<p className="text-gray-600">
						Löschen Sie Dateien aus Supabase Storage anhand von JSON-Eingaben.
					</p>
				</div>

				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-red-900">Achtung - Unwiderruflich</h3>
							<p className="text-red-800 text-sm mt-1">
								Diese Aktion löscht Dateien permanent aus dem Storage.
								Gelöschte Dateien können nicht wiederhergestellt werden.
							</p>
						</div>
					</div>
				</div>

				<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-orange-900">Development Tool</h3>
							<p className="text-orange-800 text-sm mt-1">
								Diese Seite verwendet den Supabase Service Role Key und ist nur für
								Entwicklungszwecke auf localhost verfügbar.
							</p>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
							<h3 className="text-lg font-semibold mb-4">SQL Query</h3>
							<p className="text-gray-600 text-sm mb-4">
								Mit dieser Query können Sie die Pfade für eine Organisation abrufen:
							</p>
							<pre className="bg-gray-100 rounded p-3 text-xs overflow-x-auto select-all cursor-pointer" title="Klicken zum Markieren">
{`SELECT
  bucket_name,
  array_agg(path_name) AS paths
FROM files
WHERE organization_id = ''
GROUP BY bucket_name`}
							</pre>
						</div>

						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">JSON-Eingabe</h3>
							<p className="text-gray-600 text-sm mb-4">
								Geben Sie die zu löschenden Pfade im folgenden Format ein:
							</p>
							<pre className="bg-gray-100 rounded p-3 text-xs mb-4 overflow-x-auto">
								{EXAMPLE_JSON}
							</pre>
							<textarea
								value={jsonInput}
								onChange={(e) => handleJsonChange(e.target.value)}
								className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
								placeholder="JSON hier einfügen..."
								data-testid="json-input"
								aria-label="JSON-Eingabe für zu löschende Pfade"
							/>
							{parseError && (
								<div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
									<XCircle className="w-4 h-4" />
									{parseError}
								</div>
							)}
							{jsonInput && !parseError && (
								<div className="flex items-center gap-2 mt-2 text-gray-600 text-sm">
									{getTotalPaths()} Pfade gefunden
								</div>
							)}
						</div>

						<Button
							onClick={handleDelete}
							disabled={!jsonInput.trim() || isDeleting}
							variant="destructive"
							className="w-full"
							data-testid="delete-button"
							aria-label="Dateien löschen"
						>
							{isDeleting ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Wird gelöscht...
								</>
							) : (
								<>
									<Trash2 className="w-4 h-4 mr-2" />
									Dateien löschen
								</>
							)}
						</Button>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">Ergebnisse</h3>
							{results.length === 0 ? (
								<div className="text-gray-500 text-center py-8">
									Noch keine Löschvorgänge durchgeführt
								</div>
							) : (
								<div className="flex flex-col gap-4">
									<div className="flex gap-4 text-sm">
										<div className="flex items-center gap-2 text-green-600">
											<CheckCircle2 className="w-4 h-4" />
											{successCount} erfolgreich
										</div>
										<div className="flex items-center gap-2 text-red-600">
											<XCircle className="w-4 h-4" />
											{failCount} fehlgeschlagen
										</div>
									</div>
									<div className="max-h-96 overflow-y-auto">
										<table className="w-full text-sm">
											<thead className="bg-gray-50 sticky top-0">
												<tr>
													<th className="text-left p-2">Status</th>
													<th className="text-left p-2">Bucket</th>
													<th className="text-left p-2">Pfad</th>
												</tr>
											</thead>
											<tbody>
												{results.map((result, idx) => (
													<tr key={idx} className="border-t">
														<td className="p-2">
															{result.success ? (
																<CheckCircle2 className="w-4 h-4 text-green-600" />
															) : (
																<XCircle className="w-4 h-4 text-red-600" />
															)}
														</td>
														<td className="p-2 font-mono text-xs">
															{result.bucket}
														</td>
														<td className="p-2 font-mono text-xs truncate max-w-xs" title={result.path}>
															{result.path}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
